"use client";

import { useEffect, useState } from "react";
import { setPersonalizeText, usePersonalizeText } from "@/lib/personalize-store";

/* "Put a name on it" — one line under any wall-art design, free, on the
 * digital file and on the framed print. Brian, 2026-09-06. The preview is
 * the real composition (the same code that makes the print file), rendered
 * at 600 px from the proxy copy, so what the buyer sees is what ships. */
const MAX = 40;

export function PersonalizeField({ slug, title }: { slug: string; title: string }) {
  const text = usePersonalizeText();
  const [draft, setDraft] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  /* Debounce: commit to the shared store and refresh the preview. */
  useEffect(() => {
    const t = setTimeout(() => {
      const clean = draft.replace(/\s{2,}/g, " ").trim().slice(0, MAX);
      setPersonalizeText(clean);
      if (!clean) {
        setPreviewUrl(null);
        return;
      }
      setLoading(true);
      setPreviewUrl(`/api/print/personalized?slug=${encodeURIComponent(slug)}&t=${encodeURIComponent(clean)}&preview=1`);
    }, 450);
    return () => clearTimeout(t);
  }, [draft, slug]);

  return (
    <div className="rounded-2xl border p-4 md:p-5" style={{ borderColor: text ? "var(--color-accent)" : "var(--color-border-default)", background: "var(--color-bg-elev-1)" }}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <label htmlFor="personalize" className="font-mono text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--color-accent)" }}>
          Put a name on it · free
        </label>
        <span className="font-mono text-[10px] text-white/65">{draft.length}/{MAX}</span>
      </div>
      <input
        id="personalize"
        type="text"
        maxLength={MAX}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={`e.g. Emma's Nursery · Est. 2019 · The ${title.split(" — ")[0]} Family`}
        className="mt-2 w-full rounded-xl border px-4 py-3 text-[15px] text-white outline-none focus:border-[color:var(--color-accent)]"
        style={{ borderColor: "var(--color-border-default)", background: "var(--color-bg-elev-1)" }}
      />
      <p className="mt-2 text-[12px] leading-[1.5] text-white/65">
        Printed in a clean caption band under the art — on the file you download and on any framed print. Leave it empty for the art alone.
      </p>
      {previewUrl && (
        <div className="mt-3 overflow-hidden rounded-xl border" style={{ borderColor: "var(--color-border-default)", background: "#f7f4ee" }}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={previewUrl}
            alt={`${title} with the caption “${text}”`}
            onLoad={() => setLoading(false)}
            onError={() => setLoading(false)}
            className="block w-full"
            style={{ opacity: loading ? 0.6 : 1, transition: "opacity .2s" }}
          />
        </div>
      )}
      {loading && <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.14em] text-white/65">Rendering your preview…</p>}
    </div>
  );
}
